// Modal for adding an external_managed site from the dashboard
// (added 2026-06-02 Phase 2). Calls POST /api/dashboard/external/sites
// then navigates to the new site's detail page where the operator can
// add keywords + competitors + test the push endpoint.

import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { externalApi } from "../lib/api";

export function ExternalSiteAddModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const navigate = useNavigate();
  const [domain, setDomain] = useState("");
  const [niche, setNiche] = useState("");
  const [city, setCity] = useState("");
  const [state, setState] = useState("");
  const [defaultProbeLocation, setDefaultProbeLocation] = useState("United States");
  const [publishUrl, setPublishUrl] = useState("");
  const [publishSecret, setPublishSecret] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!open) return null;

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      const r = await externalApi.create({
        domain, niche, city, state, defaultProbeLocation,
        publishUrl: publishUrl || undefined,
        publishSecret: publishSecret || undefined,
      });
      if (!r.ok) throw new Error(r.error ?? "create failed");
      onClose();
      if (r.siteId) navigate(`/sites/${r.siteId}`);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50" onClick={onClose}>
      <div className="bg-white rounded-xl border border-ink-200 max-w-xl w-full mx-4 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-4 border-b border-ink-100 flex items-baseline justify-between">
          <h2 className="text-lg font-semibold">Add external site</h2>
          <button onClick={onClose} className="text-ink-400 hover:text-ink-900">✕</button>
        </div>
        <form onSubmit={submit} className="p-5 space-y-4">
          <p className="text-xs text-ink-500">
            An external_managed site is one you own/operate but RankForge doesn't generate or deploy. RankForge will monitor rank, discover keywords from competitors, and push drafted blog posts to your site via a published API contract (<a className="text-blue-600 underline" href="https://github.com/Found-Revenue/LocalSeoLeads/blob/claude/rankforge-automation-system-QcLcm/docs/external-publish-api.md" target="_blank" rel="noreferrer">spec</a>).
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Field label="Domain *" hint="example.com (no protocol)">
              <input required value={domain} onChange={(e) => setDomain(e.target.value)} className={inputCls} placeholder="example.com" />
            </Field>
            <Field label="Default probe location" hint="DataForSEO location_name for rank checks">
              <input value={defaultProbeLocation} onChange={(e) => setDefaultProbeLocation(e.target.value)} className={inputCls} placeholder="United States" />
            </Field>
            <Field label="Niche / vertical *" hint="Used for filtering + display only">
              <input required value={niche} onChange={(e) => setNiche(e.target.value)} className={inputCls} placeholder="e.g. personal injury law" />
            </Field>
            <Field label="City *" hint="Use 'National' if not geo-anchored">
              <input required value={city} onChange={(e) => setCity(e.target.value)} className={inputCls} placeholder="Austin" />
            </Field>
            <Field label="State (2-letter) *" hint="Use 'US' if national">
              <input required maxLength={2} value={state} onChange={(e) => setState(e.target.value.toUpperCase())} className={inputCls} placeholder="TX" />
            </Field>
          </div>

          <div className="pt-2 border-t border-ink-100">
            <p className="text-xs text-ink-500 mb-2">
              <strong>Push endpoint (optional now, required to push posts).</strong> The URL on your site that receives blog posts + the HMAC shared secret (≥32 random chars). You can add these later.
            </p>
            <div className="space-y-3">
              <Field label="Publish URL" hint="Your site's POST endpoint">
                <input value={publishUrl} onChange={(e) => setPublishUrl(e.target.value)} className={inputCls} placeholder="https://example.com/api/rankforge-content" />
              </Field>
              <Field label="Publish secret" hint="HMAC-SHA256 shared secret (≥32 chars)">
                <input type="password" value={publishSecret} onChange={(e) => setPublishSecret(e.target.value)} className={inputCls} placeholder="random-string-32-chars-or-more" />
              </Field>
            </div>
          </div>

          {error && <div className="bg-rose-50 border border-rose-200 text-rose-800 px-3 py-2 rounded text-sm">{error}</div>}

          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-ink-700 hover:bg-ink-50 rounded">Cancel</button>
            <button type="submit" disabled={submitting} className="px-4 py-2 text-sm bg-ink-900 text-white rounded hover:bg-ink-700 disabled:opacity-50">
              {submitting ? "Creating…" : "Add site"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

const inputCls = "w-full px-3 py-2 border border-ink-200 rounded-md text-sm bg-white focus:outline-none focus:ring-1 focus:ring-blue-500";

function Field({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-xs uppercase tracking-wider text-ink-500 font-medium">{label}</span>
      {children}
      {hint && <span className="text-xs text-ink-400">{hint}</span>}
    </label>
  );
}
